import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import { CashInflowFilters, CashInflowPayment } from "../../utils/cashinflow.types";
import { PaymentReceipt, PaymentReceiptApplication, PaymentReceiptApplicationNew, InvoicePreviewResponse, InvoicePreviewPayload } from "../../utils/invoice.types";
import { IDeleteApiResponse, IError, apiLimit } from "../../utils/api.types";

export const fetchCashInflowPaymentsDetails = createAsyncThunk(
    'cashInflow/paymentReceiptApplication',
    async (filters: CashInflowFilters): Promise<any> => {
        let paramsArry = [];
        if (filters.selectedBuyer) {
            let q1 = ['buyer', filters.selectedBuyer];
            paramsArry.push(q1);
        }

        if (filters.selectedInvoiceStatus) {
            let q2 = ['status', filters.selectedInvoiceStatus];
            paramsArry.push(q2);
        }

        if (filters.selectedAmountRange) {
            let t = null;
            if (filters.selectedAmountRange.indexOf('-') >= 0) {
                t = filters.selectedAmountRange.split("-");
            } else if (filters.selectedAmountRange.indexOf('&') >= 0) {
                t = filters.selectedAmountRange.split("&");
            }

            if (t && t.length == 2) {
                let fromAmountRange = t[0];
                let toAmountRange = t[1];
                let q31 = ['fromAmountRange', fromAmountRange.trim()];
                let q32 = ['toAmountRange', toAmountRange.trim()];
                paramsArry.push(q31);
                paramsArry.push(q32);
            } else {
                t = filters.selectedAmountRange.split("&");
            }
        }

        if (filters.selectedPaymentMode) {
            paramsArry.push(['paymentMode', filters.selectedPaymentMode]);
        }

        if (filters.locationId) {
            paramsArry.push(['locationId', filters.locationId]);
        }

        if (filters.startDate) {
            let q4 = ['startDate', filters.startDate.getTime()];
            paramsArry.push(q4);
        }

        if (filters.endDate) {
            let q5 = ['endDate', filters.endDate.getTime()];
            paramsArry.push(q5);
        }

        // Add pagination parameters
        const limit = filters.limit || 50;
        const offset = filters.offset || 0;
        paramsArry.push(['limit', limit.toString()]);
        paramsArry.push(['offset', offset.toString()]);
        if (filters.paymentId) paramsArry.push(['paymentId', filters.paymentId]);

        const params = new URLSearchParams(paramsArry);
        const response = await axios.get(`/api/v1/invoice/payment-receipt-application`, {params});
        return response.data;
    }
);

export const addCashInflowPayment = createAsyncThunk(
    'cashInflow/addInflowPayment',
    async (data: CashInflowPayment): Promise<CashInflowPayment> => {
        const response = await axios.post('/api/v1/invoice/payment-receipt', data);
        return response.data;
    }
);

export const attachInvoiceToPayment = createAsyncThunk(
    'cashInflow/attachInvoiceToPayment',
    async (data: PaymentReceiptApplication): Promise<PaymentReceipt> => {
        const response = await axios.post('/api/v1/invoice/payment-receipt-application', data);
        return response.data;
    }
);

/**
 * Fetch Invoices Reciepts async thunk.
*/
export const fetchPaymentRecieptsByInvoiceId = createAsyncThunk(
    'cashInflow/fetchPaymentRecieptsByInvoiceId',
    async (invoiceId: string): Promise<PaymentReceiptApplicationNew> => {
        let paramsArry = [];
        if (invoiceId) {
            let q1 = ['invoiceId', invoiceId];
            paramsArry.push(q1);
        }
        const params = new URLSearchParams(paramsArry);
        const response = await axios.get(`/api/v1/invoice/payment-receipt-application`, {params});
        return response.data;
    }
);

/**
 * Payment history of a single sales invoice (each applied receipt, oldest first).
 * Used by the Paid / Partially Paid status tooltip on the invoice list.
*/
export const fetchInvoicePaymentHistory = createAsyncThunk(
    'cashInflow/fetchInvoicePaymentHistory',
    async (invoiceId: string): Promise<PaymentReceiptApplicationNew> => {
        const response = await axios.get(`/api/v1/invoice/payment-receipt-application/by-invoice/${invoiceId}`);
        return response.data;
    }
);

export const fetchInvoicePaymentRecieptPreview = createAsyncThunk<InvoicePreviewResponse,
InvoicePreviewPayload,
{
    rejectValue: IError,
}
 >(
    'invoice/payment-reciept-preview',
    async ({invoiceId, templateId}: InvoicePreviewPayload, thunkApi) => {
        
        let paramsArry = [];
        if (invoiceId) {
            let q1 = ['invoiceId',  invoiceId];
            paramsArry.push(q1);
        }
        if (templateId) {
            let q2 = ['templateId', templateId];
            paramsArry.push(q2);
        }

        const params = new URLSearchParams(paramsArry);

        try {
            const response = await axios.get(`/api/v1/invoice/payment-receipt-application/pdf/create`, {params});
            const { data } = response;
            if (response.status === 200) {
                return data;
            }
            return thunkApi.rejectWithValue(data.error);
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response.data);
        }
       
    }
);

export const deletePaymentReceipt = createAsyncThunk<IDeleteApiResponse,
string,
{
    rejectValue: IError,
}
>(
    'cashInflow/deletePaymentReceipt',
    async (paymentId: string, thunkApi) => {
        try {
            const response = await axios.delete(`/api/v1/invoice/payment-receipt/delete-entire/${paymentId}`);
            const { data } = response;
            if (response.status === 200) {
                return data;
            }
            return thunkApi.rejectWithValue(data.error);
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response.data);
        }
    }
);